<template>
    <aside ref="rootRef" class="gh-sidebar">
        <gh-menu
            :items="items"
            :active-id="activeId"
            :active-level="normalizedActiveLevel"
            :icon-size="iconSize"
            :badge-variant="badgeVariant"
            :show-arrow="showArrow"
            :collapsible="collapsible"
            :allow-multiple-expanded-parents="allowMultipleExpandedParents"
            :expanded-id-map="expandedIdMap"
            @select-item="emitItemChange"
            @toggle-item="emitToggleItem"
        >
            <template v-if="slots.content" #content="slotProps">
                <slot name="content" v-bind="slotProps"></slot>
            </template>
        </gh-menu>
    </aside>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref, useSlots, watch } from 'vue';
    import GhMenu from '@/components/base/GhMenu.vue';
    import {
        BadgeVariant,
        ScrollBehaviorMode,
        SidebarActiveScrollPosition,
        type MenuIdMap,
        type MenuItem,
        type SidebarMenuPayload,
    } from '@/types/components/base-components.types';

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            activeId?: string | number;
            activeLevel?: number;
            activeScrollBehavior?: ScrollBehaviorMode;
            activeScrollPosition?: SidebarActiveScrollPosition;
            autoScrollActive?: boolean;
            badgeVariant?: BadgeVariant;
            items?: MenuItem[];
            iconSize?: number;
            showArrow?: boolean;
            collapsible?: boolean;
            allowMultipleExpandedParents?: boolean;
            expandedIdMap?: MenuIdMap | null;
        }>(),
        {
            activeId: '',
            activeLevel: 0,
            activeScrollBehavior: ScrollBehaviorMode.Smooth,
            activeScrollPosition: SidebarActiveScrollPosition.Top,
            autoScrollActive: false,
            badgeVariant: BadgeVariant.Gray,
            items: () => [],
            iconSize: 20,
            showArrow: false,
            collapsible: true,
            allowMultipleExpandedParents: false,
            expandedIdMap: null,
        }
    );

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'change-item', payload: SidebarMenuPayload): void;
        (event: 'update:active-id-map', payload: MenuIdMap): void;
        (event: 'select-item', payload: SidebarMenuPayload): void;
        (event: 'update:expanded-id-map', payload: MenuIdMap): void;
        (event: 'toggle-item', payload: SidebarMenuPayload): void;
    }>();

    // ----- attrs -----
    const slots = useSlots();

    // ----- state -----
    const rootRef = ref<HTMLElement | null>(null);

    // ----- computed -----
    // activeLevel 防呆，確保為 0 以上整數
    const normalizedActiveLevel = computed(() => {
        const parsedLevel = Number(props.activeLevel);
        if (Number.isNaN(parsedLevel) || parsedLevel < 0) {
            return 0;
        }

        return Math.floor(parsedLevel);
    });

    // activeScrollPosition 防呆，僅允許 top/center
    const normalizedActiveScrollPosition = computed(() => {
        return props.activeScrollPosition === SidebarActiveScrollPosition.Center
            ? SidebarActiveScrollPosition.Center
            : SidebarActiveScrollPosition.Top;
    });

    // ----- methods -----
    // active 目標優先使用 activeLevel，避免階層選單命中父層造成捲動偏移
    const findActiveLinkElement = (
        listElement: HTMLElement
    ): HTMLElement | null => {
        const activeByLevelElement = listElement.querySelector(
            `.gh-menu__item--level-${normalizedActiveLevel.value}.is-active > .gh-menu__link`
        );
        if (activeByLevelElement instanceof HTMLElement) {
            return activeByLevelElement;
        }

        const activeElement = listElement.querySelector(
            '.gh-menu__item.is-active > .gh-menu__link'
        );
        if (activeElement instanceof HTMLElement) {
            return activeElement;
        }

        return null;
    };

    // 以可視區域座標計算相對位移，rect 無效時回退 offsetTop 邏輯
    const resolveElementTopWithinList = (
        listElement: HTMLElement,
        activeElement: HTMLElement
    ): number => {
        const listRect = listElement.getBoundingClientRect();
        const activeRect = activeElement.getBoundingClientRect();

        const isRectAvailable =
            Number.isFinite(listRect.top) &&
            Number.isFinite(activeRect.top) &&
            listRect.height > 0 &&
            activeRect.height > 0;
        if (isRectAvailable) {
            return listElement.scrollTop + (activeRect.top - listRect.top);
        }

        return activeElement.offsetTop - listElement.offsetTop;
    };

    // Sidebar 維持既有 change-item 事件，並同步 select-item payload
    const emitItemChange = (payload: SidebarMenuPayload): void => {
        if (!payload || !payload.itemId) {
            return;
        }

        const activeIdMap = payload.activeIdMap
            ? { ...payload.activeIdMap }
            : {};
        const expandedIdMap = payload.expandedIdMap
            ? { ...payload.expandedIdMap }
            : {};

        emits('change-item', {
            item: payload.item || null,
            itemId: payload.itemId,
            level: payload.level,
            activeIdMap,
            expandedIdMap,
        });
        emits('update:active-id-map', activeIdMap);
        emits('select-item', payload);
    };

    // children 展開/收合時同步回傳 expanded map
    const emitToggleItem = (payload: SidebarMenuPayload): void => {
        if (!payload) {
            return;
        }

        const expandedIdMap = payload.expandedIdMap
            ? { ...payload.expandedIdMap }
            : {};
        emits('update:expanded-id-map', expandedIdMap);
        emits('toggle-item', payload);
    };

    // active 項目自動捲動處理 (top/center)
    const scrollActiveItemIntoView = (): void => {
        if (!props.autoScrollActive) return;

        requestAnimationFrame(() => {
            const listElement = rootRef.value
                ? rootRef.value.querySelector('.gh-menu__list--level-0')
                : null;

            if (!(listElement instanceof HTMLElement)) {
                return;
            }

            const activeElement = findActiveLinkElement(listElement);
            if (!(activeElement instanceof HTMLElement)) {
                return;
            }

            // 只控制 sidebar 內層 scroll，避免影響 body 捲軸
            let targetScrollTop = resolveElementTopWithinList(
                listElement,
                activeElement
            );
            if (
                normalizedActiveScrollPosition.value ===
                SidebarActiveScrollPosition.Center
            ) {
                targetScrollTop =
                    targetScrollTop -
                    (listElement.clientHeight - activeElement.clientHeight) / 2;
            }

            try {
                listElement.scrollTo({
                    top: Math.max(targetScrollTop, 0),
                    behavior: props.activeScrollBehavior,
                });
            } catch {
                listElement.scrollTop = Math.max(targetScrollTop, 0);
            }
        });
    };

    // ----- watch -----
    watch(
        () => props.activeId,
        () => {
            scrollActiveItemIntoView();
        }
    );

    watch(
        () => props.activeLevel,
        () => {
            scrollActiveItemIntoView();
        }
    );

    watch(
        () => props.activeScrollPosition,
        () => {
            scrollActiveItemIntoView();
        }
    );

    watch(
        () => props.items,
        () => {
            scrollActiveItemIntoView();
        },
        {
            deep: true,
        }
    );

    // ----- lifecycle -----
    onMounted(() => {
        scrollActiveItemIntoView();
    });
</script>

<style scoped lang="scss">
    .gh-sidebar {
        width: 100%;
        max-height: 100%;
        padding: 12px 8px;
        display: flex;
        flex-direction: column;
        align-self: stretch;
        overflow: hidden;
        box-sizing: border-box;
    }
</style>
